import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { modifyModal, openModal } from "../redux/action/modal";
import { openConfirmModal, modifyConfirmModal } from "../redux/action/confirm";
import { showToastAlert } from "../redux/action/alert";
import { registerValidationSchema } from "../common/validation";
import {
  succeedToSubmit,
  failToSubmit,
  failAlert,
  successAlert,
} from "../common/alert";
import { uidConfirm } from "../common/confirm";
import Layout from "../components/layout/Layout";
import { checkUidConfirm, submitRegisterForm } from "../api/Register";
import PolicyModal from "../components/checkout/policyModal";
import { getUserInfo } from "../api/getUserInfo";
import { isLoading, isLoaded } from "../redux/action/loading";
import * as R from "../components/register/register.styles";
import * as gtag from "../lib/gtag";
import { autoPhoneFormat } from "../common/util/checkout-validation";
import { cerPhoneNumberCheck, cerPhoneTokenCheck } from "../api/certification";
import { random } from "lodash";
import { useCheckUser } from "../hook/useCheckUser";
import { SeoMeta } from "@/utils/seo/SeoMeta";
import { useBannerStore } from "@store/useBannerStore";

function Register({
  openModal,
  modifyModal,
  showToastAlert,
  openConfirmModal,
  modifyConfirmModal,
  uidConfirm,
  isLoading,
  isLoaded,
  banners,
}) {
  const router = useRouter();
  const { bannerList, interview = [] } = useBannerStore((s) => s);
  const [isNotUser, setIsNotUser] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(false);
  const [policyDivision, setPolicyDivision] = useState();
  const [isBoxChecked, setIsBoxChecked] = useState([false, false, false]);
  const [isConfirmBtnActive, setIsConfirmBtnActive] = useState(true);
  const [prevUidInput, setPrevUidInput] = useState("");
  const [channelInfo, setChannelInfo] = useState("");
  const [isCheckedPhone, setIsCheckedPhone] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isCheckingTime, setIsCheckingTime] = useState(false);
  const [checkValidTime, setCheckValidTime] = useState("");
  const [interviewBanner, setInterviewBanner] = useState("");

  const formOptions = {
    resolver: yupResolver(registerValidationSchema),
    mode: "onChange",
  };

  const { register, handleSubmit, formState, reset, getValues, watch } =
    useForm(formOptions);

  const currentInputValues = getValues();
  const { errors } = formState;

  // 유저 체크
  const { checkUser } = useCheckUser({ isLoading, isLoaded, setIsNotUser });

  // 약관 동의 체크
  const checkboxHandler = (id) => (e) => {
    const tempArr = [...isBoxChecked];
    tempArr[id] = e.target.checked;
    setIsBoxChecked(tempArr);
  };

  // 아이디 중복확인 API 핸들
  const uidDuplicationHandler = async () => {
    if (currentInputValues.uid == "") {
      return failAlert("아이디를 입력해주세요.");
    }

    const body = {
      modes: "IdSearch",
      uid: currentInputValues.uid,
    };

    if (errors.uid) return;

    const response = await checkUidConfirm(body);

    if (response.Code === "0000") {
      setPrevUidInput(currentInputValues.uid);
      setIsConfirmBtnActive(false);
      modifyConfirmModal(uidConfirm(response.Data.Msg));
      return openConfirmModal();
    } else {
      modifyConfirmModal(
        uidConfirm("기존 아이디와 같거나 사용하실 수 없는 아이디 입니다.")
      );
      return openConfirmModal();
    }
  };

  // 아이디 중복확인 버튼 활성화 유무
  const isUidConfirmed = () => {
    if (watch("uid") !== prevUidInput) {
      setIsConfirmBtnActive(true);
    }
  };

  // 인증번호 타임 체크
  const checkValidTimer = () => {
    if (isCheckingTime) return;
    setIsCheckingTime(true);
    let time = 300;
    let timer = setInterval(() => {
      if (time >= 0) {
        const min = String(Math.floor(time / 60)).padStart(2, "0");
        const sec = String(time % 60).padStart(2, "0");
        setCheckValidTime(min + ":" + sec);
        time -= 1;
      } else {
        clearInterval(timer);
        setIsCheckingTime(false);
        setIsCheckingPhone(false);
      }
    }, 1000);
  };

  // 인증번호 발송
  const checkValidPhone = async () => {
    if (watch("phone") == "" || watch("phone").length == 0) {
      return failAlert("연락처를 입력해주세요.");
    }

    const body = {
      modes: "Citation",
      phone: watch("phone"),
    };

    const data = await cerPhoneNumberCheck(body);

    if (data?.code === "0000") {
      setIsCheckingPhone(true);
      checkValidTimer();
    }
    if (data?.code === "9999") {
      failAlert("인증번호 발송에 실패했습니다.");
    }
  };

  // 인증번호 검증
  const checkValidPhoneToken = async () => {
    if (watch("phoneToken") == undefined || watch("phoneToken").length != 6) {
      return failAlert("인증번호를 다시 확인해주세요.");
    }

    const body = {
      modes: "Check",
      phone: watch("phone"),
      num: watch("phoneToken"),
    };
    const data = await cerPhoneTokenCheck(body);

    if (data?.code === "0000") {
      successAlert(data?.data?.Msg);
      setIsCheckingPhone(false);
      setIsCheckedPhone(true);
    }
    if (data?.code === "9999") {
      failAlert("잘못된 인증번호입니다");
    }
    if (data?.code === "9990") {
      failAlert("만료되었거나 없는 인증코드입니다");
    }
  };

  // 약관동의 모달
  const showTermsModal = (num) => {
    setOpenPolicy((prev) => !prev);
    setPolicyDivision(num);
  };

  // 회원정보 제출
  const submitHandler = async () => {
    if (isBoxChecked.includes(false)) {
      return showToastAlert("필수 항목에 동의해주세요.", failToSubmit);
    } else if (isConfirmBtnActive === true) {
      return showToastAlert("아이디 중복확인이 필요합니다.", failToSubmit);
    } else if (isCheckedPhone === false) {
      return showToastAlert("휴대폰 인증이 필요합니다.", failToSubmit);
    }
    const currentInputValues = getValues();

    const body = {
      modes: "Join",
      uid: currentInputValues.uid,
      upass: currentInputValues.upass,
      reupass: currentInputValues.reupass,
      company: currentInputValues.company,
      name: currentInputValues.name,
      email: currentInputValues.email,
      phone: watch("phone"),
      aid: sessionStorage.getItem("channel") ? currentInputValues.aid : "",
      usages: currentInputValues.usages,
    };

    if (channelInfo && (!body.aid || body.aid === "")) {
      return failAlert("제휴 아이디를 입력해주세요.");
    }

    isLoading();

    const response = await submitRegisterForm(body);

    if (response === "Success") {
      showToastAlert("회원가입에 성공하였습니다.", succeedToSubmit);
      // 채널톡 가입 이벤트
      window.ChannelIO("track", "SignUp", {
        name: body.name,
        email: body.email,
      });

      var UserAgent = navigator.userAgent;
      var widerplanet_device =
        UserAgent.match(
          /iPhone|iPod|Android|Windows CE|BlackBerry|Symbian|Windows Phone|webOS|Opera Mini|Opera Mobi|POLARIS|IEMobile|lgtelecom|nokia|SonyEricsson/i
        ) != null || UserAgent.match(/LG|SAMSUNG|Samsung/) != null
          ? "mobile"
          : "web";
      var wptg_tagscript_vars = globalThis.wptg_tagscript_vars || [];
      wptg_tagscript_vars.push(function () {
        return {
          wp_hcuid: "",
          ti: "54644",
          ty: "Join" /*트래킹태그 타입 */,
          device: widerplanet_device /*디바이스 종류  (web 또는  mobile)*/,
          items: [
            {
              i: "회원 가입" /*전환 식별 코드  (한글 , 영어 , 번호 , 공백 허용 )*/,
              t: "회원 가입" /*전환명  (한글 , 영어 , 번호 , 공백 허용 )*/,
              p: "1" /*전환가격  (전환 가격이 없을 경우 1로 설정 )*/,
              q: "1" /*전환수량  (전환 수량이 고정적으로 1개 이하일 경우 1로 설정 )*/,
            },
          ],
        };
      });
      globalThis.dataLayer.push({ event: "sign_up" });
      globalThis.wptg_tagscript?.exec();
      if (!wcs_add) var wcs_add = {};
      wcs_add["wa"] = "s_53d3817a0ea3";
      var _nasa = {};
      if (window.wcs) {
        _nasa["cnv"] = wcs.cnv("2", "1");
        wcs_do(_nasa);
      }
      var _nasa = {};

      // ENP 회원가입
      window.CallMtm({
        productName: "", //광고주 측에서 설정하고 싶은 값(default convType)
        convType: "join", //etc, join, login
        click: "",
      });

      reset();

      isLoaded();
      return router.push("/");
    } else {
      isLoaded();
      return showToastAlert(response, failToSubmit);
    }
  };

  const checkAlliance = () => {
    const info = sessionStorage.getItem("channelInfo");
    if (info) {
      setChannelInfo(info);
    }
  };

  useEffect(() => {
    console.log(window);

    setIsConfirmBtnActive(false);
    checkboxHandler();
    checkUser();
    checkAlliance();
  }, []);

  useEffect(() => {
    isUidConfirmed();
  }, [watch("uid")]);

  useEffect(() => {
    if (interview) {
      setInterviewBanner(interview[random(0, interview.length - 1)]);
    }
  }, [bannerList]);

  return (
    <>
      <Layout
        parent="홈"
        sub="회원가입"
        pageTitle={SeoMeta.signUp.title}
        pageDesc={SeoMeta.signUp.description}
        pageKeyword={SeoMeta.signUp.keywords}
      >
        <div className="page-content mt-50 pb-150">
          {isNotUser && (
            <div className="container">
              <div className="row">
                <div className="m-auto">
                  <div className="row">
                    <div className="col-lg-6 pr-30 d-none d-lg-block">
                      {interviewBanner && (
                        <img
                          className="border-radius-15"
                          src={interviewBanner?.P_FileUrl}
                          alt={interviewBanner?.img_alt}
                        />
                      )}
                    </div>
                    <div className="col-lg-6 col-md-10 m-auto">
                      <div className="login_wrap widget-taber-content background-white">
                        <div className="padding_eight_all bg-white">
                          <div className="heading_s1">
                            <h1 className="mb-30">회원가입</h1>
                          </div>
                          <form onSubmit={handleSubmit(submitHandler)}>
                            <div className="mb-3">
                              {channelInfo &&
                                channelInfo?.split("+")[0] != "" && (
                                  <div className="form-group">
                                    <input
                                      required=""
                                      type="text"
                                      name="aid"
                                      placeholder={`${
                                        channelInfo?.split("+")[0]
                                      } 아이디를 입력해주세요.`}
                                      {...register("aid")}
                                      className={`${
                                        errors.aid
                                          ? "input-invalid-red"
                                          : "form-input"
                                      }`}
                                    />
                                    {!watch("aid") && (
                                      <p className="input-invalid-msg">
                                        {"제휴 아이디를 입력해주세요."}
                                      </p>
                                    )}
                                  </div>
                                )}
                              <R.IdWrapper variable="75">
                                <div className="inputWrapper">
                                  <input
                                    required=""
                                    type="text"
                                    name="uid"
                                    {...register("uid")}
                                    placeholder="아이디"
                                    autoComplete="off"
                                    className={`${
                                      errors.uid
                                        ? "input-invalid-red"
                                        : "form-input"
                                    }`}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={uidDuplicationHandler}
                                  className="btn uid-confirmation-btn"
                                  disabled={isConfirmBtnActive ? false : true}
                                >
                                  중복확인
                                </button>
                              </R.IdWrapper>
                              {errors.uid && (
                                <p className="input-invalid-msg">
                                  {errors.uid?.message}
                                </p>
                              )}
                            </div>
                            <R.PasswordWrapper>
                              <div className="pwInputWrapper">
                                <input
                                  required=""
                                  type="password"
                                  name="upass"
                                  placeholder="비밀번호"
                                  {...register("upass")}
                                  autoComplete="new-password"
                                  className={`${
                                    errors.upass
                                      ? "input-invalid-red"
                                      : "form-input"
                                  }`}
                                />
                                {errors.upass && (
                                  <p className="input-invalid-msg">
                                    {errors.upass?.message}
                                  </p>
                                )}
                              </div>
                              <div className="pwInputWrapper">
                                <input
                                  required=""
                                  type="password"
                                  name="reupass"
                                  placeholder="비밀번호 확인"
                                  {...register("reupass")}
                                  autoComplete="off"
                                  className={`${
                                    errors.reupass
                                      ? "input-invalid-red"
                                      : "form-input"
                                  }`}
                                />
                                {errors.reupass && (
                                  <p className="input-invalid-msg">
                                    {errors.reupass?.message}
                                  </p>
                                )}
                              </div>
                            </R.PasswordWrapper>
                            <div className="form-group">
                              <input
                                // required=""
                                type="text"
                                name="company"
                                placeholder="회사명(선택)"
                                {...register("company")}
                                autoComplete="off"
                                className="form-input"
                                // className={`${
                                //   errors.company
                                //     ? "input-invalid-red"
                                //     : "form-input"
                                // }`}
                              />
                              {/* {errors.company && (
                                <p className="input-invalid-msg">
                                  {errors.company?.message}
                                </p>
                              )} */}
                            </div>
                            <R.NameEmailWrapper>
                              <div className="nameInputWrapper">
                                <input
                                  required=""
                                  type="text"
                                  name="name"
                                  placeholder="담당자명"
                                  {...register("name")}
                                  className={`${
                                    errors.name
                                      ? "input-invalid-red"
                                      : "form-input"
                                  }`}
                                />
                                {errors.name && (
                                  <p className="input-invalid-msg">
                                    {errors.name?.message}
                                  </p>
                                )}
                              </div>
                              <div className="emailInputWrapper">
                                <input
                                  required=""
                                  type="email"
                                  name="email"
                                  placeholder="이메일"
                                  {...register("email")}
                                  className={`${
                                    errors.email
                                      ? "input-invalid-red"
                                      : "form-input"
                                  }`}
                                />
                                {errors.email && (
                                  <p className="input-invalid-msg">
                                    {errors.email?.message}
                                  </p>
                                )}
                              </div>
                            </R.NameEmailWrapper>
                            <R.PhoneWrapper>
                              <div className="inputBtnWrapper">
                                <div
                                  className={
                                    isCheckedPhone === true ||
                                    isCheckingPhone === true
                                      ? "inputWrapper isConfirmed"
                                      : "inputWrapper"
                                  }
                                >
                                  <input
                                    required=""
                                    type="text"
                                    name="phone"
                                    placeholder="연락처 (-를 제외한 숫자만)"
                                    {...register("phone")}
                                    autoComplete="off"
                                    className={`${
                                      errors.phone
                                        ? "input-invalid-red"
                                        : "form-input"
                                    }`}
                                    maxLength={13}
                                    disabled={
                                      isCheckedPhone === true ||
                                      isCheckingPhone === true
                                        ? true
                                        : false
                                    }
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={checkValidPhone}
                                  className="btn uid-confirmation-btn"
                                  disabled={
                                    Boolean(errors.phone) === true ||
                                    isCheckingPhone === true ||
                                    isCheckedPhone === true
                                      ? true
                                      : false
                                  }
                                >
                                  인증번호 발송
                                </button>
                              </div>
                              {errors.phone && (
                                <p className="input-invalid-msg">
                                  {errors.phone?.message}
                                </p>
                              )}
                            </R.PhoneWrapper>
                            {isCheckingPhone && (
                              <R.PhoneCheckedWrapper>
                                <div className="inputBtnWrapper">
                                  <div className="inputWrapper">
                                    <input
                                      required=""
                                      type="text"
                                      name="phone"
                                      placeholder="인증번호 입력"
                                      {...register("phoneToken")}
                                      autoComplete="off"
                                      className="form-input"
                                      maxLength={6}
                                    />
                                    {isCheckingPhone && (
                                      <span className="timeCounter">
                                        {checkValidTime}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={checkValidPhoneToken}
                                    className="btn uid-confirmation-btn"
                                  >
                                    인증하기
                                  </button>
                                </div>
                                <div>
                                  인증번호는 카카오 알림톡 혹은 문자로
                                  전송됩니다.
                                </div>
                              </R.PhoneCheckedWrapper>
                            )}
                            <R.UsagesWrapper>
                              <div className="infoWrapper">
                                <span className="subTitle">
                                  포장하시는 상품 정보(선택)
                                </span>
                                <span className="example">{`예시) 화장품, 의류`}</span>
                              </div>
                              <input
                                // required=""
                                type="text"
                                name="phone"
                                // placeholder="모두 입력해주세요."
                                {...register("usages")}
                                autoComplete="off"
                                className="form-input"
                              />
                            </R.UsagesWrapper>
                            {/* 만 14세 이상 동의 */}
                            <div className="login_footer form-group">
                              <div className="chek-form">
                                <div className="custome-checkbox d-flex align-itmes-center">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="checkbox"
                                    id="terms-agreement-0"
                                    value=""
                                    checked={isBoxChecked[0]}
                                    onChange={checkboxHandler(0)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="terms-agreement-0"
                                  >
                                    <span>(필수) 만 14세 이상 동의</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                            {/* 이용약관 동의 */}
                            <div className="login_footer form-group">
                              <div className="chek-form">
                                <div className="custome-checkbox d-flex align-itmes-center">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="checkbox"
                                    id="terms-agreement-1"
                                    value=""
                                    checked={isBoxChecked[1]}
                                    onChange={checkboxHandler(1)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="terms-agreement-1"
                                  >
                                    <span>(필수) 이용약관 동의</span>
                                  </label>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => showTermsModal(1)}
                                className="register-agreement-btn d-flex align-items-center"
                              >
                                <i className="fi-rs-book-alt mr-5 text-muted"></i>
                                <span className="text-muted">약관보기</span>
                              </button>
                            </div>
                            {/* 개인정보 수집 이용동의 */}
                            <div className="login_footer form-group mb-50">
                              <div className="chek-form">
                                <div className="custome-checkbox d-flex align-itmes-center">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="checkbox"
                                    id="terms-agreement-2"
                                    value=""
                                    checked={isBoxChecked[2]}
                                    onChange={checkboxHandler(2)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="terms-agreement-2"
                                  >
                                    <span>(필수) 개인정보 수집 이용동의</span>
                                  </label>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => showTermsModal(0)}
                                className="register-agreement-btn d-flex align-items-center"
                              >
                                <i className="fi-rs-book-alt mr-5 text-muted"></i>
                                <span className="text-muted">약관보기</span>
                              </button>
                            </div>
                            <div className="form-group">
                              <button
                                type="submit"
                                className="btn btn-heading btn-block hover-up col-12"
                                name="login"
                              >
                                회원가입
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {openPolicy && (
          <PolicyModal
            policy={policyDivision}
            openPolicyModal={openPolicy}
            setOpenPolicyModal={showTermsModal}
          />
        )}
      </Layout>
    </>
  );
}
const mapStateToProps = (state) => ({
  banners: state.banners,
});

const mapDispatchToProps = {
  openModal,
  modifyModal,
  openConfirmModal,
  modifyConfirmModal,
  uidConfirm,
  showToastAlert,
  isLoading,
  isLoaded,
};

export default connect(mapStateToProps, mapDispatchToProps)(Register);
